import { Box, styled, Typography } from "@material-ui/core";
import { useRxData } from "rxdb-hooks";
import type { EpocusReferral } from "../../../../../global-providers/rxdb-provider/collections/epocus-referral";
import {
  type EpocusReferralQueryConstructor,
  RXDB_EPOCUS_REFERRAL_COLLECTION_NAME,
} from "../../../../../global-providers/rxdb-provider/collections/epocus-referral";
import type { EpocusReferralOutcome } from "../../../../../global-providers/rxdb-provider/collections/epocus-referral-outcome";
import { RXDB_EPOCUS_REFERRAL_OUTCOME_COLLECTION_NAME } from "../../../../../global-providers/rxdb-provider/collections/epocus-referral-outcome";
import {
  EPOCUS_BORDER,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../../_lib/epocus-palette";
import { epocusTheme } from "../../../_lib/epocus-theme-provider";
import { HARDCODED_RECEIVERS } from "../../../referrals/_lib/hardcoded-receivers";
import { ReferralStatusChip } from "../../../referrals/_lib/referral-status-chip";

const TimelineCard = styled(Box)({
  backgroundColor: EPOCUS_WHITE,
  border: `1px solid ${EPOCUS_BORDER}`,
  borderRadius: 10,
  padding: "14px 16px",
  display: "flex",
  flexDirection: "column",
  gap: 10,
});

const FieldLabel = styled(Typography)({
  fontSize: rem(11),
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: EPOCUS_TEXT_SECONDARY,
});

const OutcomeSummary = styled(Box)({
  borderTop: `1px solid ${EPOCUS_BORDER}`,
  paddingTop: 10,
  display: "flex",
  flexDirection: "column",
  gap: 8,
  [epocusTheme.breakpoints.up("sm")]: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 16,
  },
});

const ACTION_LABELS: Record<string, string> = {
  admitted: "Admitted",
  treatedDischarged: "Treated and discharged",
  referredOnward: "Referred onward",
  noAction: "No action required",
};

const DIAGNOSIS_LABELS: Record<string, string> = {
  yes: "Confirmed",
  no: "Not confirmed",
  unable: "Unable to determine",
};

const URGENCY_LABELS: Record<string, string> = {
  routine: "Routine",
  urgent: "Urgent",
  emergency: "Emergency",
};

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString("en-GB", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const ReferralTimelineCard: FC<{
  referral: EpocusReferral;
  outcome: EpocusReferralOutcome | null;
}> = ({ referral, outcome }) => {
  const receiver = HARDCODED_RECEIVERS.find(
    (r) => r.id === referral.receivingUserId,
  );

  return (
    <TimelineCard>
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        style={{ gap: 8 }}
      >
        <Box>
          <Typography
            style={{
              fontWeight: 600,
              fontSize: rem(15),
              color: EPOCUS_TEXT_PRIMARY,
            }}
          >
            Referred to{" "}
            {receiver ? `${receiver.name} (${receiver.role})` : "clinician"}
          </Typography>
          <Typography
            variant="body2"
            style={{
              fontSize: rem(13),
              color: EPOCUS_TEXT_SECONDARY,
              marginTop: 2,
            }}
          >
            {URGENCY_LABELS[referral.urgency]} · {formatDate(referral.sentAt)}
          </Typography>
        </Box>
        <ReferralStatusChip status={referral.status} />
      </Box>

      <Typography style={{ fontSize: rem(14), color: EPOCUS_TEXT_PRIMARY }}>
        {referral.clinicalReason}
      </Typography>

      {outcome && (
        <OutcomeSummary>
          <Box>
            <FieldLabel>Diagnosis</FieldLabel>
            <Typography
              style={{ fontSize: rem(14), color: EPOCUS_TEXT_PRIMARY }}
            >
              {DIAGNOSIS_LABELS[outcome.diagnosisConfirmed]}
            </Typography>
          </Box>
          <Box>
            <FieldLabel>Action</FieldLabel>
            <Typography
              style={{ fontSize: rem(14), color: EPOCUS_TEXT_PRIMARY }}
            >
              {ACTION_LABELS[outcome.actionTaken]}
            </Typography>
          </Box>
          {outcome.clinicalNotes && (
            <Box style={{ flexBasis: "100%" }}>
              <FieldLabel>Notes</FieldLabel>
              <Typography
                style={{ fontSize: rem(14), color: EPOCUS_TEXT_PRIMARY }}
              >
                {outcome.clinicalNotes}
              </Typography>
            </Box>
          )}
        </OutcomeSummary>
      )}
    </TimelineCard>
  );
};

export const EncounterTimeline: FC<{ encounterId: string }> = ({
  encounterId,
}) => {
  const query: EpocusReferralQueryConstructor = (col) =>
    col?.find({ selector: { encounterId: { $eq: encounterId } } });

  const { result: rawReferrals } = useRxData<EpocusReferral>(
    RXDB_EPOCUS_REFERRAL_COLLECTION_NAME,
    query,
  );
  const referrals = [...(rawReferrals ?? [])].sort((a, b) =>
    b.sentAt.localeCompare(a.sentAt),
  );

  const referralIds = referrals.map((r) => r.id);

  const { result: outcomes } = useRxData<EpocusReferralOutcome>(
    RXDB_EPOCUS_REFERRAL_OUTCOME_COLLECTION_NAME,
    (col) =>
      referralIds.length > 0
        ? col?.find({ selector: { referralId: { $in: referralIds } } })
        : col?.find({ selector: { referralId: { $eq: "" } } }),
  );

  const outcomeByReferralId = new Map(
    (outcomes ?? []).map((o) => [o.referralId, o]),
  );

  if (referrals.length === 0) {
    return (
      <Box
        p={3}
        style={{
          backgroundColor: EPOCUS_WHITE,
          border: `1px solid ${EPOCUS_BORDER}`,
          borderRadius: 10,
          textAlign: "center",
        }}
      >
        <Typography
          style={{
            color: EPOCUS_TEXT_SECONDARY,
            fontSize: rem(15),
            fontStyle: "italic",
          }}
        >
          No referrals yet for this encounter.
        </Typography>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
      {referrals.map((r) => (
        <ReferralTimelineCard
          key={r.id}
          referral={r}
          outcome={outcomeByReferralId.get(r.id) ?? null}
        />
      ))}
    </Box>
  );
};
